import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  Skeleton,
  Pagination,
  FormControl,
  Select,
  Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ErrorPage from "../Components/ErrorPage";
import AccountAdd from "./AccountAdd";
import AccountEdit from "./AccountEdit";
import AccountView from "./AccountView";
import useFetchObjects from "../../api/useFetchObjects";
import useDelete from "../../api/useDelete";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";

import { formatJalaliDateTime } from "../utils/dateFormat";
const openingBalanceCellSx = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) {
    return {};
  }
  if (n > 0) {
    return { color: "success.main", fontWeight: 600 };
  }
  return { color: "error.main", fontWeight: 600 };
};

const getDisplayBalance = (row) => {
  const name = (row?.name || "").trim().toLowerCase();
  const raw = row?.balance != null ? row.balance : row?.opening_balance;
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  const abs = Math.abs(n);
  if (name === "purchase afg clearing") return -abs;
  if (name === "main") return abs;
  return n;
};

const AccountIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const [openView, setOpenView] = useState(false);
  const [viewData, setViewData] = useState(null);

  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["accounts", searchTerm, page, rowsPerPage],
    `shop/accounts/?search=${searchTerm}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  useEffect(() => {
    refetch();
  }, [searchTerm, page, rowsPerPage, refetch]);

  const { handleDelete, ConfirmDialog } = useDelete(
    "shop/accounts",
    token
  );

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  const rows = data && data.results ? data.results : data || [];
  const totalCount = data && data.count ? data.count : rows.length;

  if (isError) {
    return <ErrorPage message={t("account.failedToLoad")} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h4">{t("account.accountList")}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenAdd(true)}
        >
          {t("account.addNew")}
        </Button>
      </Box>

      <Typography variant="subtitle1" color="textSecondary" mb={2}>
        {t("account.dataLoadedOn")} {formatJalaliDateTime()}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <TextField
          variant="outlined"
          fullWidth
          placeholder={t("account.searchPlaceholder")}
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table aria-label="shop accounts table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>
                {t("account.index")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("account.name")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("account.currency")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("account.openingBalance")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("account.shop")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("account.note")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("account.action")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, i) => (
                  <TableRow key={`skeleton-${i}`}>
                    {Array.from(new Array(7)).map((__, j) => (
                      <TableCell key={`skeleton-${i}-${j}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : rows.map((row, i) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{(page - 1) * rowsPerPage + i + 1}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>
                      {row.currency_title || row.currency?.title || "-"}
                    </TableCell>
                    <TableCell sx={openingBalanceCellSx(getDisplayBalance(row))}>
                      {getDisplayBalance(row) ?? "-"}
                    </TableCell>
                    <TableCell>
                      {row.shop_name || row.shop?.name || "-"}
                    </TableCell>
                    <TableCell>{row.note || "-"}</TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => {
                          setViewData(row);
                          setOpenView(true);
                        }}
                      >
                        View
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => {
                          setEditData(row);
                          setOpenEdit(true);
                        }}
                      >
                        {t("account.edit")}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(row.id)}
                      >
                        {t("account.delete")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid
        container
        justifyContent="center"
        alignItems="center"
        padding="10px"
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography>{t("account.rowsPerPage")}</Typography>
          <FormControl
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
              },
            }}
            size="small"
          >
            <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage)}
          page={page}
          onChange={handlePageChange}
          variant="outlined"
          color="primary"
          sx={{ ml: 2 }}
        />
      </Grid>

      <ConfirmDialog />

      {openAdd && (
        <AccountAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          token={token}
        />
      )}

      {openEdit && (
        <AccountEdit
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          editData={editData}
          token={token}
        />
      )}
      {openView && (
        <AccountView
          open={openView}
          onClose={() => setOpenView(false)}
          data={viewData}
          token={token}
        />
      )}
    </Box>
  );
};

export default AccountIndex;
